import React from 'react';
import { SidebarItem } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useState, useEffect } from 'react';
import { IconComponent } from '@backstage/core-plugin-api';

interface PermissionAwareSidebarItemProps {
  icon: IconComponent;
  to: string;
  text: string;
  allowedUsers?: string[]; // Optional - if not provided, everyone can access
}

export const PermissionAwareSidebarItem: React.FC<PermissionAwareSidebarItemProps> = ({
  icon,
  to,
  text,
  allowedUsers,
}) => {
  const identityApi = useApi(identityApiRef);
  const [hasAccess, setHasAccess] = useState(true); // Default to true for unrestricted pages
  const [loading, setLoading] = useState(!!allowedUsers); // Only loading if we need to check permissions

  useEffect(() => {
    // If no allowedUsers provided, everyone has access
    // If allowedUsers is an empty array, no one has access
    if (!allowedUsers) {
      setHasAccess(true);
      setLoading(false);
      return;
    }
    
    if (allowedUsers.length === 0) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    const checkAccess = async () => {
      try {
        const { userEntityRef } = await identityApi.getBackstageIdentity();
        const username = userEntityRef.split('/')[1]; // Extract username from user:default/username
        
        if (allowedUsers.includes(username)) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Failed to get user identity:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [identityApi, allowedUsers]);

  // Don't render anything while loading or if user doesn't have access
  if (loading || !hasAccess) {
    return null;
  }

  return <SidebarItem icon={icon} to={to} text={text} />;
};
